import React, { FC, Fragment } from 'react';

import { GetFeedQueryVariables, GroupFragment } from 'api';
import { FeedViewOptions } from 'types/app';
import { Desktop, EmptyState, Layouts, Mobile } from 'components';
import { useCapabilities, useFollowed } from 'hooks';
import { FeedList } from 'features/feed';

import DesktopBanner from '../DesktopBanner';
import MobileBanner from '../MobileBanner';
import CreatePostBanner from './CreatePostBanner';
import Members from './Members';
import GroupPostsGrid from './GroupPostsGrid';

interface Props {
  isFollowing: boolean;
  group: GroupFragment;
  variables: GetFeedQueryVariables;
  activeView: FeedViewOptions;
  onViewChange: () => void;
}

const GroupPosts: FC<Props> = ({ group, isFollowing, variables, activeView, onViewChange }) => {
  const { postable } = useFollowed();
  const { needs } = useCapabilities();

  const { list } = postable;
  const selectedGroup = getSelectedGroup();
  const emptyStateName = selectedGroup ? 'groupMember' : 'groupJoin';

  function getSelectedGroup() {
    return list.find(postableGroup => {
      return group.id === postableGroup.id;
    });
  }

  function getFeedHeader() {
    const canPostToGroup = group.allow_user_posts || needs('Access.Moderator', group.id);

    if (!canPostToGroup) {
      return null;
    }

    return <CreatePostBanner group={selectedGroup} />;
  }

  function renderFeedList() {
    return (
      <FeedList
        emptyState={() => <EmptyState box name={emptyStateName} />}
        variables={variables}
        selectedGroup={group}
        header={getFeedHeader()}
      />
    );
  }

  function renderFeedSidebar() {
    return <Members group={group} />;
  }

  function renderGroupHeader() {
    return (
      <Fragment>
        <Desktop>
          <DesktopBanner activeView={activeView} onViewChange={onViewChange} group={group} />
        </Desktop>
        <Mobile>
          <MobileBanner isFollowing={isFollowing} group={group} />
        </Mobile>
      </Fragment>
    );
  }

  if (activeView === FeedViewOptions.Grid) {
    return (
      <GroupPostsGrid
        emptyState={<EmptyState box name={emptyStateName} />}
        feedHeader={getFeedHeader()}
        variables={variables}
        group={group}
        header={renderGroupHeader()}
      />
    );
  }

  if (activeView === FeedViewOptions.List) {
    return (
      <Layouts.AppMain2Column
        desktopSplit="8/4"
        header={renderGroupHeader()}
        left={renderFeedList()}
        right={renderFeedSidebar()}
      />
    );
  }

  return null;
};

export default GroupPosts;
